import { cloneElement, useCallback, useMemo } from "react";
import { HashRouter, Link, Route, Switch, Redirect } from "react-router-dom";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import classnames from "classnames";

import {
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DateFnsUtils from "@date-io/date-fns";

import { BuildingSelector, Layout } from "./ui/components";
import {
  LoginPage,
  UsersPage,
  ApartmentsPage,
  BuildingsPage,
  CommunicationsPage,
  CommunicationGroupsPage,
  AmenitiesPage,
  AmenitiesDefinitionsPage,
  ReservationsPage,
  ServicesPage,
  ServiceReservationsPage,
  ContactsPage,
  CouponsPage,
  AppVersionsPage,
  CSIPage,
} from "./ui/pages";

import {
  useApplicationMenu,
  useContestualServiceRoutes,
  useLoggedUserInfo,
  useWindowLocationHref,
} from "./hooks";
import {
  useCultureState,
  useUserDispatch,
  useUserState,
  setUserLoggedOut,
} from "./context";

import useAppStyle from "./App.style";

/* DATE FNS Date localizations */
import enLocale from "date-fns/locale/en-US";
import itLocale from "date-fns/locale/it";

const cultureToLocaleMap = {
  en: enLocale,
  it: itLocale,
};

/* -- Depenendencies initializations -- */
i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    resources: {
      en: {
        translation: require("./i18n/en.json"),
      },
      it: {
        translation: require("./i18n/it.json"),
      },
    },
    lng: "en", // if you're using a language detector, do not define the lng option
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

/* App Component */

function App() {
  //const { t, i18n } = useTranslation();
  //i18n.changeLanguage('it');
  const { serviceCalendarRoutes } = useContestualServiceRoutes();
  const { isAuthenticated } = useUserState();
  const userDispatch = useUserDispatch();

  const { isBuildingManagerUser, isAdminUser } = useLoggedUserInfo();
  const { currentCulture } = useCultureState();
  const { applicationMenuItems } = useApplicationMenu();
  const { windowLocationHref } = useWindowLocationHref();
  const classes = useAppStyle();

  const landingRoute = useMemo(
    () => (isAuthenticated ? "/app" : "/login"),
    [isAuthenticated]
  );

  const muiPickersUtilityProviderLanguage = useMemo(() => {
    let language = cultureToLocaleMap["en"];
    if (currentCulture) {
      language = cultureToLocaleMap[currentCulture] ?? cultureToLocaleMap["en"];
    }

    return language;
  }, [currentCulture]);

  const activeApplicationMenuListItem = useMemo(() => {
    const appSection = windowLocationHref.substring(
      windowLocationHref.indexOf("#") + 1
    );
    return applicationMenuItems.find((menuElement) => {
      return appSection === menuElement.route.trim();
    });
  }, [applicationMenuItems, windowLocationHref]);

  const applicationMenuListItems = useMemo(() => {
    return applicationMenuItems.map((menuElement, index) => {
      const isActive = activeApplicationMenuListItem === menuElement;
      return (
        <ListItem
          key={index}
          button
          component={Link}
          to={menuElement.route}
          disableRipple
          classes={{
            root: classnames({
              [classes.linkActive]: isActive,
            }),
          }}
        >
          <ListItemIcon>{menuElement.icon}</ListItemIcon>
          <ListItemText primary={menuElement.label} />
        </ListItem>
      );
    });
  }, [applicationMenuItems, activeApplicationMenuListItem, classes.linkActive]);

  const logOut = useCallback(() => {
    setUserLoggedOut(userDispatch);
  }, [userDispatch]);

  const headerActionIconButtons = useMemo(() => {
    const actionIconButtons: any = [];
    if (isAuthenticated) {
      // Logout Action
      actionIconButtons.push(
        <IconButton key={"logout-button"} color="inherit" onClick={logOut}>
          <ExitToAppIcon />
        </IconButton>
      );
    }

    return actionIconButtons;
  }, [isAuthenticated, logOut]);

  const headerActions = useMemo(() => {
    if (isBuildingManagerUser || isAdminUser) {
      return (
        <div
          style={{
            width: "350px",
          }}
        >
          <BuildingSelector
            onUserBuildingChange={() => window.location.reload()}
          />
        </div>
      );
    }

    return void 0;
  }, [isAdminUser, isBuildingManagerUser]);

  const { isServiceProviderUser } = useLoggedUserInfo();

  const defaultRedirectTo = useMemo(() => {
    if (isAuthenticated && isServiceProviderUser && serviceCalendarRoutes) {
      if (serviceCalendarRoutes.length > 0) {
        // console.log('-> returning default route:');
        // console.log(serviceCalendarRoutes[0].routePath);
        return serviceCalendarRoutes[0].routePath;
      } else {
        return "/app/service_reservations";
      }
    }

    return "/app/reservations";
  }, [isAuthenticated, isServiceProviderUser, serviceCalendarRoutes]);

  return (
    <MuiPickersUtilsProvider
      utils={DateFnsUtils}
      locale={muiPickersUtilityProviderLanguage}
    >
      <HashRouter>
        <Switch>
          <Route exact path="/" render={() => <Redirect to={landingRoute} />} />
          <Route
            path="/login"
            render={() => {
              return <LoginPage />;
            }}
          />
          <Route
            path="/app"
            render={() => {
              if (isAuthenticated) {
                return (
                  <Layout
                    sidebarProps={{
                      listItems: applicationMenuListItems,
                    }}
                    headerProps={{
                      title: "Titiro",
                      actions: headerActions,
                      icons: headerActionIconButtons,
                    }}
                  >
                    <Switch>
                      <Route exact path="/app">
                        <Redirect to={defaultRedirectTo} />
                      </Route>
                      <Route path="/app/users" component={UsersPage} />
                      <Route path="/app/amenities" component={AmenitiesPage} />
                      <Route
                        path="/app/app-versions"
                        component={AppVersionsPage}
                      />
                      <Route
                        path="/app/amenities_definitions"
                        component={AmenitiesDefinitionsPage}
                      />
                      <Route
                        path="/app/communications"
                        component={CommunicationsPage}
                      />
                      <Route
                        path="/app/communication_groups"
                        component={CommunicationGroupsPage}
                      />
                      <Route path="/app/coupons" component={CouponsPage} />
                      <Route path="/app/csi" component={CSIPage} />
                      <Route
                        path="/app/reservations"
                        component={ReservationsPage}
                      />
                      <Route
                        path="/app/apartments"
                        component={ApartmentsPage}
                      />
                      <Route path="/app/buildings" component={BuildingsPage} />
                      <Route path="/app/services" component={ServicesPage} />
                      <Route
                        path="/app/service_reservations"
                        component={ServiceReservationsPage}
                      />
                      <Route path="/app/contacts" component={ContactsPage} />
                      {serviceCalendarRoutes?.map((v, index) =>
                        cloneElement(v.route, { key: `route-${index}` })
                      ) ?? []}
                    </Switch>
                  </Layout>
                );
              } else {
                return <Redirect to="/login" />;
              }
            }}
          />
        </Switch>
      </HashRouter>
    </MuiPickersUtilsProvider>
  );
}

export default App;
